// DigitalAdvisory.jsx — rebuilt with ITAdvisory design system
import React, { useEffect, useRef, useState } from 'react';
import './DigitalAdvisory.css';
// import digital from "../../../assets/services/digital.png"; // re-enable when available

import {
  IoRocketOutline,
  IoStatsChartOutline,
  IoFlashOutline,
  IoBusinessOutline,
  IoPeopleOutline,
  IoShieldCheckmarkOutline,
  IoBulbOutline,
  IoCloudOutline,
  IoAnalyticsOutline,
  IoHappyOutline,
  IoBarChartOutline,
  IoArrowForwardOutline,
  IoMedkitOutline,
} from 'react-icons/io5';
import {
  FiTrendingUp,
  FiLayers,
  FiServer,
  FiCloud,
  FiTarget,
  FiMap,
  FiRefreshCw,
  FiCpu,
  FiUsers,
  FiGrid,
} from 'react-icons/fi';
import {
  MdOutlineAnalytics,
  MdOutlineAutoAwesome,
  MdOutlineDashboard,
  MdOutlinePsychology,
  MdOutlineRocketLaunch,
  MdOutlinePrecisionManufacturing,
  MdOutlineLocalShipping,
  MdOutlineStorefront,
  MdOutlineAccountBalance,
} from 'react-icons/md';

/* ── Data ─────────────────────────────────────────────── */

const deliverables = [
  { title: "Enterprise digital transformation strategy", icon: <FiTarget size={22} /> },
  { title: "Business process modernization", icon: <FiRefreshCw size={22} /> },
  { title: "Legacy system transformation", icon: <FiServer size={22} /> },
  { title: "Digital operating model design", icon: <FiLayers size={22} /> },
  { title: "Platform transformation advisory", icon: <IoCloudOutline size={22} /> },
  { title: "Customer experience transformation", icon: <IoHappyOutline size={22} /> },
  { title: "Automation & workflow optimization", icon: <MdOutlineAutoAwesome size={22} /> },
  { title: "Enterprise technology assessment", icon: <IoAnalyticsOutline size={22} /> },
  { title: "Change management & transformation governance", icon: <IoShieldCheckmarkOutline size={22} /> },
  { title: "Transformation roadmap & execution advisory", icon: <FiMap size={22} /> },
];

const impactAreas = [
  { title: "Operational Efficiency", icon: <IoStatsChartOutline size={28} />, desc: "Streamline processes and eliminate waste across the enterprise" },
  { title: "Technology Modernization", icon: <FiCloud size={28} />, desc: "Upgrade legacy systems and adopt future-ready platforms" },
  { title: "Customer Experience", icon: <IoHappyOutline size={28} />, desc: "Deliver seamless, personalised digital interactions at scale" },
  { title: "Data-Driven Decisions", icon: <MdOutlineAnalytics size={28} />, desc: "Turn raw data into strategic intelligence and competitive edge" },
  { title: "Enterprise Agility", icon: <IoFlashOutline size={28} />, desc: "Build adaptive capabilities that respond faster to market shifts" },
];

const industries = [
  { name: "Manufacturing", icon: <MdOutlinePrecisionManufacturing size={24} /> },
  { name: "Lifesciences", icon: <IoMedkitOutline size={24} /> },
  { name: "Retail & E-commerce", icon: <MdOutlineStorefront size={24} /> },
  { name: "Logistics & Supply Chain", icon: <MdOutlineLocalShipping size={24} /> },
  { name: "Financial Services", icon: <MdOutlineAccountBalance size={24} /> },
  { name: "GCC & Enterprise Ops", icon: <IoBusinessOutline size={24} /> },
];

const whyBairees = [
  { text: "Business-first transformation approach", icon: <IoBusinessOutline size={22} /> },
  { text: "Strategy aligned with measurable outcomes", icon: <IoBarChartOutline size={22} /> },
  { text: "Deep expertise across operations, AI, data, and enterprise technology", icon: <FiCpu size={22} /> },
  { text: "Scalable transformation frameworks built for complexity", icon: <FiLayers size={22} /> },
  { text: "Advisory-to-execution partnership model", icon: <IoPeopleOutline size={22} /> },
];

const stats = [
  { number: "200+", label: "Transformations Delivered" },
  { number: "18+", label: "Industries Served" },
  { number: "94%", label: "Client Retention Rate" },
  { number: "3×", label: "Avg. ROI Multiplier" },
];

/* ── Counter hook ──────────────────────────────────────────── */
function useCountUp(target, duration = 1800, start = false) {
  const [value, setValue] = useState(0);
  useEffect(() => {
    if (!start) return;
    let startTime = null;
    const step = (ts) => {
      if (!startTime) startTime = ts;
      const progress = Math.min((ts - startTime) / duration, 1);
      setValue(Math.floor(progress * target));
      if (progress < 1) requestAnimationFrame(step);
    };
    requestAnimationFrame(step);
  }, [start, target, duration]);
  return value;
}

function StatCard({ number, label, delay, inView }) {
  // Parse number to get numeric value and suffix
  const numericValue = parseInt(number);
  const suffix = number.replace(numericValue.toString(), '');
  const count = useCountUp(numericValue, 1600, inView);
  
  return (
    <div className="da-stat" style={{ animationDelay: `${delay}ms` }}>
      <span className="da-stat__number">{count}{suffix}</span>
      <span className="da-stat__label">{label}</span>
    </div>
  );
}

/* ── Component ────────────────────────────────────────── */

const DigitalAdvisory = () => {
  const statsRef = useRef(null);
  const [statsVisible, setStatsVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setStatsVisible(true); },
      { threshold: 0.3 }
    );
    if (statsRef.current) observer.observe(statsRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <div className="ita">

      {/* ── HERO ─────────────────────────────────────────── */}
      <section className="ita-hero">
        <div className="ita-hero__bg-grid" />

        <div className="ita-hero__container">
          {/* Left copy */}
          <div className="ita-hero__copy">
            <p className="ita-hero__eyebrow">Digital Transformation Advisory</p>

            <h1 className="ita-hero__title">
              Transform Operations.<br />
              <span className="ita-hero__title--gold">Accelerate Growth.</span>
            </h1>

            <p className="ita-hero__tagline">
              Strategy · Modernisation · Execution
            </p>

            <p className="ita-hero__desc">
              Digital transformation is no longer just a technology initiative — it is a business
              imperative. Bairees helps organisations modernise operations, optimise customer
              experiences, and build scalable digital capabilities that deliver measurable outcomes.
            </p>

            <p className="ita-hero__desc">
              We align technology, processes, people, and data into a transformation roadmap
              engineered for long-term competitive advantage.
            </p>

            <div className="ita-hero__actions">
              <a href="/contact">
                <button className="ita-btn ita-btn--primary">
                  Start Your Transformation <IoArrowForwardOutline size={16} />
                </button>
              </a>
              <a href="/contact">
                <button className="ita-btn ita-btn--ghost">
                  View Our Framework
                </button>
              </a>
            </div>
          </div>

          {/* Right aside cards */}
          <div className="ita-hero__aside">
            {[
              { icon: <MdOutlineRocketLaunch size={22} />, label: "Approach", value: "Business-First Strategy" },
              { icon: <FiTarget size={22} />, label: "Focus", value: "Measurable Outcomes" },
              { icon: <IoBulbOutline size={22} />, label: "Methodology", value: "Advisory to Execution" },
            ].map((c, i) => (
              <div className="ita-hero__card" key={i}>
                <div className="ita-hero__card-icon">{c.icon}</div>
                <div className="ita-hero__card-label">{c.label}</div>
                <div className="ita-hero__card-value">{c.value}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── STATS BAR ─────────────────────────────────────── */}
      <div className="ita-stats" ref={statsRef}>
        <div className="ita-stats__inner">
          {stats.map((s, i) => (
            <React.Fragment key={i}>
              <StatCard {...s} delay={i * 150} inView={statsVisible} />
              {i < stats.length - 1 && <div className="ita-stats__divider" />}
            </React.Fragment>
          ))}
        </div>
      </div>

      {/* ── WHAT WE DELIVER ────────────────────────────────── */}
      <section className="ita-section ita-section--alt">
        <div className="ita-section__container">
          <div className="ita-section__header">
            <p className="ita-eyebrow">What We Deliver</p>
            <h2 className="ita-section__title">End-to-End Transformation Services</h2>
            <p className="ita-section__subtitle">
              From strategy to execution, every engagement is scoped to move the needle on
              outcomes that matter most to your business.
            </p>
          </div>

          <div className="ita-deliverables">
            {deliverables.map((d, i) => (
              <div className="ita-deliverable" key={i}>
                <span className="ita-deliverable__icon">{d.icon}</span>
                <span className="ita-deliverable__text">{d.title}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FRAMEWORK ─────────────────────────────────────── */}
      <section className="ita-framework">
        <div className="ita-framework__container">
          <div className="ita-framework__header">
            <p className="ita-eyebrow ita-eyebrow--light">Our Methodology</p>
            <h2 className="ita-framework__title">Digital Transformation Framework</h2>
          </div>

          {/* Inline framework diagram — replace with <img src={digital} … /> when ready */}
          <div className="ita-framework__visual ita-framework__diagram">
            <div className="ita-diagram__track">
              {[
                { step: "01", label: "Assess", sub: "Current-state audit" },
                { step: "02", label: "Strategise", sub: "Roadmap & priorities" },
                { step: "03", label: "Design", sub: "Operating model" },
                { step: "04", label: "Execute", sub: "Delivery & governance" },
                { step: "05", label: "Scale", sub: "Sustain & optimise" },
              ].map((s, i) => (
                <div className="ita-diagram__step" key={i}>
                  <div className="ita-diagram__step-num">{s.step}</div>
                  <div className="ita-diagram__step-label">{s.label}</div>
                  <div className="ita-diagram__step-sub">{s.sub}</div>
                  {i < 4 && <div className="ita-diagram__arrow" />}
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* ── IMPACT + INDUSTRIES ────────────────────────────── */}
      <section className="ita-section">
        <div className="ita-section__container">
          <div className="ita-two-col">

            {/* Impact */}
            <div>
              <p className="ita-eyebrow">How We Create Impact</p>
              <h2 className="ita-panel__title">Five Dimensions of Transformation Value</h2>
              <ul className="ita-list">
                {impactAreas.map((a, i) => (
                  <li className="ita-list__item" key={i}>
                    <span className="ita-list__icon">{a.icon}</span>
                    <div>
                      <div className="ita-list__text" style={{ fontWeight: 600, marginBottom: 2 }}>{a.title}</div>
                      <div className="ita-list__text" style={{ fontWeight: 400, opacity: 0.7, fontSize: 13 }}>{a.desc}</div>
                    </div>
                  </li>
                ))}
              </ul>
            </div>

            {/* Industries */}
            <div>
              <p className="ita-eyebrow">Industries We Support</p>
              <h2 className="ita-panel__title">Cross-Sector Expertise</h2>
              <div className="ita-industry-grid">
                {industries.map((ind, i) => (
                  <div className="ita-industry-card" key={i}>
                    <span className="ita-industry-card__icon">{ind.icon}</span>
                    <span className="ita-industry-card__name">{ind.name}</span>
                  </div>
                ))}
              </div>
            </div>

          </div>
        </div>
      </section>

      {/* ── WHY BAIREES ────────────────────────────────────── */}
      <section className="ita-why">
        <div className="ita-why__container">
          <div className="ita-why__header">
            <p className="ita-eyebrow ita-eyebrow--light">Why Bairees</p>
            <h2 className="ita-why__title">The Bairees Difference</h2>
            <p className="ita-why__subtitle">
              We don't just advise — we partner from strategy through delivery, ensuring
              every transformation investment translates to tangible business performance.
            </p>
          </div>

          <div className="ita-why__grid">
            {whyBairees.map((w, i) => (
              <div className="ita-why__card" key={i}>
                <div className="ita-why__card-icon">{w.icon}</div>
                <p className="ita-why__card-text">{w.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── CTA ────────────────────────────────────────────── */}
      <section className="ita-section ita-section--alt">
        <div className="ita-section__container">
          <div className="ita-cta">
            <p className="ita-eyebrow">Get Started</p>
            <h2 className="ita-cta__title">Ready to Accelerate Your Digital Transformation?</h2>
            <p className="ita-cta__desc">
              Bairees helps organisations move from disconnected systems and slow processes
              to scalable digital operations built for sustained growth.
            </p>
            <div className="ita-cta__actions">
              <a href="/contact">
                <button className="ita-btn ita-btn--primary">
                  Book a Discovery Call <IoArrowForwardOutline size={16} />
                </button>
              </a>
            </div>
            <div className="ita-cta__chips">
              <span>No commitment required</span>
              <span>·</span>
              <span>60-min strategy session</span>
              <span>·</span>
              <span>Tailored to your context</span>
            </div>
          </div>
        </div>
      </section>

    </div>
  );
};

export default DigitalAdvisory;